import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { z } from 'zod';
import { FieldSeparator } from '@bliks-io/ui';
import { OrgFunctionSuggestionPayloadSchema, type OrgFunctionKind } from '@bliks-io/contracts';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { FormInput, FormTextarea, FormDepartmentSelect } from '@/components/form/rhf';
import { EntitySuggestionDialog } from './EntitySuggestionDialog';
import type { SuggestionPickEvent } from './shared/suggestionPickEvent';

// Controlled inputs emit '' when blank, which z.email().optional() rejects, and
// the department select emits '' when nothing is chosen (department is optional
// on a OrgFunction). Accept '' for both here; toApiPayload coerces to undefined.
//
// Factory function so the email message is translated at validation time, not at
// import time (mirrors the bulk-onboarding schemas).
const createOrgFunctionSuggestionFormSchema = (t: TFunction) =>
  OrgFunctionSuggestionPayloadSchema.extend({
    email: z.email(t('errors.validation.email')).optional().or(z.literal('')),
    department: z.string().optional().or(z.literal(''))
  });
type OrgFunctionSuggestionFormData = z.infer<
  ReturnType<typeof createOrgFunctionSuggestionFormSchema>
>;

export interface OrgFunctionSuggestionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName: string;
  /**
   * The kind being suggested — implied by the picker surface that opened the
   * dialog (team list → team, function list → role), never user-selectable.
   * Fixed at suggestion time: a misclassified suggestion is rejected and
   * re-suggested, not re-kinded (ADR 0004).
   */
  kind: OrgFunctionKind;
  /**
   * Source-link refinement: exactly one of these must be set so the backend
   * can attach the suggestion to its originating recording or process.
   */
  sourceRecordingId?: string;
  /** Process behind a node-anchored source — cache-seed hint, not wire data. */
  seedProcessId?: string;
  /** Draft-editor origin: create/link post to the draft suggestion routes. */
  draftProcessId?: string;
  onUseExistingEntity?: (entityId: string) => void;
  /** Called after a suggestion is created or adopted (discriminated on `kind`). */
  onSuggestionCreated?: (event: SuggestionPickEvent) => void;
}

export function OrgFunctionSuggestionDialog({
  open,
  onOpenChange,
  initialName,
  kind,
  sourceRecordingId,
  seedProcessId,
  draftProcessId,
  onUseExistingEntity,
  onSuggestionCreated
}: OrgFunctionSuggestionDialogProps) {
  const { t } = useTranslation();
  const schema = useMemo(() => createOrgFunctionSuggestionFormSchema(t), [t]);
  const copyPrefix =
    kind === 'team'
      ? 'entitySuggestion.orgFunction.dialog.team'
      : 'entitySuggestion.orgFunction.dialog.role';

  const defaultValues = useMemo<OrgFunctionSuggestionFormData>(
    () => ({
      name: initialName,
      kind,
      description: '',
      department: '',
      email: ''
    }),
    [initialName, kind]
  );

  return (
    <EntitySuggestionDialog<OrgFunctionSuggestionFormData>
      open={open}
      onOpenChange={onOpenChange}
      sourceRecordingId={sourceRecordingId}
      seedProcessId={seedProcessId}
      draftProcessId={draftProcessId}
      onUseExistingEntity={onUseExistingEntity}
      onSuggestionCreated={onSuggestionCreated}
      kind="orgFunction"
      icon={ENTITY_ICONS[kind]}
      titleKey={`${copyPrefix}.title`}
      descriptionKey={`${copyPrefix}.description`}
      submitLabelKey="entitySuggestion.orgFunction.dialog.submit"
      schema={schema}
      defaultValues={defaultValues}
      primaryFieldName="name"
      secondaryFieldName="department"
      buildCandidatesPayload={({ name, secondaryValue }) => ({
        name,
        department: secondaryValue
      })}
      toApiPayload={(data) => ({
        name: data.name.trim(),
        kind: data.kind,
        description: data.description?.trim() || undefined,
        department: data.department?.trim() || undefined,
        email: data.email?.trim() || undefined
      })}
      duplicateCandidatesPanelKind="orgFunction"
      renderFields={({ form, candidatesPanel }) => (
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-4">
            <FormInput
              control={form.control}
              name="name"
              label={t(`${copyPrefix}.name`)}
              required
            />
            {candidatesPanel}
          </div>

          <div className="flex flex-col gap-4">
            <FieldSeparator>{t('organization.departments.relationships')}</FieldSeparator>
            <FormDepartmentSelect
              control={form.control}
              name="department"
              label={t('entitySuggestion.orgFunction.dialog.department')}
              labelHint={t('entitySuggestion.orgFunction.dialog.departmentHint')}
            />
            <FormInput
              control={form.control}
              name="email"
              label={t('entitySuggestion.orgFunction.dialog.email')}
              type="email"
            />
          </div>

          <div className="flex flex-col gap-4">
            <FieldSeparator>{t('common.Details')}</FieldSeparator>
            <FormTextarea
              control={form.control}
              name="description"
              label={t('entitySuggestion.orgFunction.dialog.description_field')}
              rows={3}
            />
          </div>
        </div>
      )}
    />
  );
}
